import { Box, Chip, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RuleIcon from "@mui/icons-material/Rule";
import TranslateIcon from "@mui/icons-material/Translate";
import { EditButton, useRecordContext } from "react-admin";
import {
    HorizontalContainer,
    LeftWorkspacePanel,
    RightWorkspacePanel,
    SectionHeader,
    SectionTitle,
    ShowLabel,
    ShowValue,
    ThreeColGrid,
    WorkspacePanel,
    WorkspacePanelsWrapper,
    WorkspaceSection,
    WorkSpaceTitleRow,
} from "../../styles/commonStyles";
import { colors } from "../../styles/colors";
import { formatDateTimeWithSeconds } from "../../utils/DateTimeUtils";
import { ReasonSubReasonResponse } from "../../interfaces/ReasonSubReason/ReasonSubReasonResponse";

const getSourceOfClient = (clientId: number | null | undefined): string => {
    if (clientId === 2) return "CENTAURUS";
    if (clientId === 4) return "CUSTOMER APP";
    if (clientId === 9) return "WEBSITE";
    return clientId != null ? String(clientId) : "-";
};

const renderBoolean = (value?: boolean | null) => {
    if (value === undefined || value === null) return <ShowValue>-</ShowValue>;
    return (
        <Chip
            label={value ? "Yes" : "No"}
            size="small"
            sx={{
                backgroundColor: value ? "#d4edda" : "#f8d7da",
                color: value ? "#155724" : "#721c24",
                fontWeight: 500,
            }}
        />
    );
};

const ReasonSubreasonMetaShow = () => {
    const record = useRecordContext<ReasonSubReasonResponse>();

    if (!record) return null;

    return (
        <div>
            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    mb: 2,
                    border: "1px solid #e8ebed",
                    borderRadius: 2,
                    background:
                        "linear-gradient(135deg, rgba(25, 117, 210, 0.06) 0%, rgba(25, 117, 210, 0.0) 60%)",
                }}
            >
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    alignItems={{ xs: "flex-start", md: "center" }}
                    justifyContent="space-between"
                    gap={2}
                >
                    <Stack direction="row" alignItems="center" gap={2}>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "rgba(25, 117, 210, 0.12)",
                                color: colors.primary,
                            }}
                        >
                            <RuleIcon />
                        </Box>
                        <Box>
                            <Stack direction="row" alignItems="center" gap={1}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {record.reason || "Untitled reason"}
                                </Typography>
                                <Tooltip title="Copy ID">
                                    <IconButton
                                        size="small"
                                        onClick={() => navigator.clipboard.writeText(String(record.id))}
                                        sx={{ p: 0.25 }}
                                    >
                                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                                {record.subReason || "—"}
                                {record.module ? ` · ${record.module}` : ""}
                                {record.actionType ? ` · ${record.actionType}` : ""}
                                {` · #${record.id}`}
                            </Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                        <Chip
                            label={record.isActive ? "Active" : "Inactive"}
                            size="small"
                            sx={{
                                backgroundColor: record.isActive ? "#d4edda" : "#f8d7da",
                                color: record.isActive ? "#155724" : "#721c24",
                                fontWeight: 500,
                            }}
                        />
                        <Chip
                            label={record.isVisible ? "Visible" : "Hidden"}
                            size="small"
                            sx={{
                                backgroundColor: record.isVisible ? "#d1ecf1" : "#e2e3e5",
                                color: record.isVisible ? "#0c5460" : "#383d41",
                                fontWeight: 500,
                            }}
                        />
                        {record.shouldCreateEscalation && (
                            <Chip
                                label="Escalates"
                                size="small"
                                sx={{ backgroundColor: "#fff3cd", color: "#856404", fontWeight: 500 }}
                            />
                        )}
                        {(record.markLeave ?? record.markleave) && (
                            <Chip
                                label="Marks Leave"
                                size="small"
                                sx={{ backgroundColor: "#ffe0b2", color: "#874a01", fontWeight: 500 }}
                            />
                        )}
                        <EditButton />
                    </Stack>
                </Stack>
            </Paper>

            <WorkspacePanelsWrapper>
                <LeftWorkspacePanel>
                    <WorkspacePanel>
                        <WorkspaceSection>
                            <SectionHeader>
                                <WorkSpaceTitleRow>
                                    <RuleIcon sx={{ color: colors.primary, fontSize: 24 }} />
                                    <SectionTitle>Reason Details</SectionTitle>
                                </WorkSpaceTitleRow>
                            </SectionHeader>
                        </WorkspaceSection>

                        <ThreeColGrid>
                            <HorizontalContainer>
                                <ShowLabel style={{ marginBottom: "0px" }}>#</ShowLabel>
                                <ShowLabel style={{ marginBottom: "0px" }}>ID:</ShowLabel>
                                <ShowValue>{record?.id}</ShowValue>
                                <Tooltip title="Copy ID">
                                    <IconButton
                                        size="small"
                                        onClick={() => navigator.clipboard.writeText(String(record?.id))}
                                        sx={{ p: 0.25 }}
                                    >
                                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Tooltip>
                            </HorizontalContainer>
                            <HorizontalContainer>
                                <ShowLabel style={{ marginBottom: "0px" }}>Client Id:</ShowLabel>
                                <ShowValue>
                                    {record?.clientId != null
                                        ? `${record.clientId} (${getSourceOfClient(record.clientId)})`
                                        : "-"}
                                </ShowValue>
                            </HorizontalContainer>
                            <HorizontalContainer>
                                <ShowLabel style={{ marginBottom: "0px" }}>Module:</ShowLabel>
                                <ShowValue>{record?.module || "-"}</ShowValue>
                            </HorizontalContainer>
                        </ThreeColGrid>

                        <ThreeColGrid>
                            <HorizontalContainer>
                                <ShowLabel style={{ marginBottom: "0px" }}>Reason:</ShowLabel>
                                <ShowValue>{record?.reason || "-"}</ShowValue>
                            </HorizontalContainer>
                            <HorizontalContainer>
                                <ShowLabel style={{ marginBottom: "0px" }}>Sub Reason:</ShowLabel>
                                <ShowValue>{record?.subReason || "-"}</ShowValue>
                            </HorizontalContainer>
                            <HorizontalContainer>
                                <ShowLabel style={{ marginBottom: "0px" }}>Action Type:</ShowLabel>
                                <ShowValue>{record?.actionType || "-"}</ShowValue>
                            </HorizontalContainer>
                        </ThreeColGrid>
                    </WorkspacePanel>

                    <WorkspacePanel>
                        <WorkspaceSection>
                            <SectionHeader>
                                <WorkSpaceTitleRow>
                                    <SectionTitle>Behaviour & Penalties</SectionTitle>
                                </WorkSpaceTitleRow>
                            </SectionHeader>
                        </WorkspaceSection>

                        <ThreeColGrid>
                            <HorizontalContainer>
                                <ShowLabel style={{ marginBottom: "0px" }}>Refund %:</ShowLabel>
                                <ShowValue>
                                    {record?.refundPercentage != null ? `${record.refundPercentage}%` : "-"}
                                </ShowValue>
                            </HorizontalContainer>
                            <HorizontalContainer>
                                <ShowLabel style={{ marginBottom: "0px" }}>Cook Penalty:</ShowLabel>
                                <ShowValue>
                                    {record?.cookPenaltyAmount ?? record?.markPenalty ?? "-"}
                                </ShowValue>
                            </HorizontalContainer>
                            <HorizontalContainer>
                                <ShowLabel style={{ marginBottom: "0px" }}>Internal Rating Param Id:</ShowLabel>
                                <ShowValue>{record?.internalRatingParamId ?? "-"}</ShowValue>
                            </HorizontalContainer>
                        </ThreeColGrid>

                        <ThreeColGrid>
                            <HorizontalContainer>
                                <ShowLabel style={{ marginBottom: "0px" }}>WhatsApp Template Id:</ShowLabel>
                                <ShowValue>{record?.whatsappTemplateId ?? "-"}</ShowValue>
                            </HorizontalContainer>
                        </ThreeColGrid>
                    </WorkspacePanel>
                </LeftWorkspacePanel>

                <RightWorkspacePanel>
                    <WorkspacePanel>
                        <WorkspaceSection>
                            <SectionHeader>
                                <WorkSpaceTitleRow>
                                    <SectionTitle>Flags</SectionTitle>
                                </WorkSpaceTitleRow>
                            </SectionHeader>
                        </WorkspaceSection>

                        <Stack gap={1.25} sx={{ mt: 2.5 }}>
                            <HorizontalContainer>
                                <ShowLabel style={{ marginBottom: "0px", minWidth: 140 }}>Active:</ShowLabel>
                                {renderBoolean(record?.isActive)}
                            </HorizontalContainer>
                            <HorizontalContainer>
                                <ShowLabel style={{ marginBottom: "0px", minWidth: 140 }}>Visible:</ShowLabel>
                                {renderBoolean(record?.isVisible)}
                            </HorizontalContainer>
                            <HorizontalContainer>
                                <ShowLabel style={{ marginBottom: "0px", minWidth: 140 }}>Create Escalation:</ShowLabel>
                                {renderBoolean(record?.shouldCreateEscalation)}
                            </HorizontalContainer>
                            <HorizontalContainer>
                                <ShowLabel style={{ marginBottom: "0px", minWidth: 140 }}>Mark Leave:</ShowLabel>
                                {renderBoolean(record?.markLeave ?? record?.markleave)}
                            </HorizontalContainer>
                        </Stack>
                    </WorkspacePanel>

                    <WorkspacePanel>
                        <WorkspaceSection>
                            <SectionHeader>
                                <WorkSpaceTitleRow>
                                    <TranslateIcon sx={{ color: colors.primary, fontSize: 22 }} />
                                    <SectionTitle>Translations</SectionTitle>
                                </WorkSpaceTitleRow>
                            </SectionHeader>
                        </WorkspaceSection>

                        <Stack gap={1.25}>
                            <HorizontalContainer>
                                <ShowLabel style={{ marginBottom: "0px", minWidth: 80 }}>English:</ShowLabel>
                                <ShowValue>{record?.trEn ?? record?.subReason ?? "-"}</ShowValue>
                            </HorizontalContainer>
                            <HorizontalContainer>
                                <ShowLabel style={{ marginBottom: "0px", minWidth: 80 }}>Hindi:</ShowLabel>
                                <ShowValue>{record?.trHi ?? "-"}</ShowValue>
                            </HorizontalContainer>
                            <HorizontalContainer>
                                <ShowLabel style={{ marginBottom: "0px", minWidth: 80 }}>Bangla:</ShowLabel>
                                <ShowValue>{record?.trBn ?? "-"}</ShowValue>
                            </HorizontalContainer>
                        </Stack>
                    </WorkspacePanel>

                    <WorkspacePanel>
                        <WorkspaceSection>
                            <SectionHeader>
                                <WorkSpaceTitleRow>
                                    <SectionTitle>Timestamps</SectionTitle>
                                </WorkSpaceTitleRow>
                            </SectionHeader>
                        </WorkspaceSection>
                        <Stack gap={1.25}>
                            <HorizontalContainer>
                                <ShowLabel style={{ marginBottom: "0px", minWidth: 80 }}>Created:</ShowLabel>
                                <ShowValue>
                                    {record?.createdAt ? formatDateTimeWithSeconds(record.createdAt) : "-"}
                                </ShowValue>
                            </HorizontalContainer>
                            <HorizontalContainer>
                                <ShowLabel style={{ marginBottom: "0px", minWidth: 80 }}>Updated:</ShowLabel>
                                <ShowValue>
                                    {record?.updatedAt ? formatDateTimeWithSeconds(record.updatedAt) : "-"}
                                </ShowValue>
                            </HorizontalContainer>
                        </Stack>
                    </WorkspacePanel>
                </RightWorkspacePanel>
            </WorkspacePanelsWrapper>
        </div>
    );
};

export default ReasonSubreasonMetaShow;
